import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useReducer, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";
import { debugLogs } from "@/lib/debugLogs";
import { usePreferences } from "@/lib/preferences";
const LEVEL_STYLES = {
    debug: "text-slate-400",
    info: "text-sky-400",
    warn: "text-amber-400",
    error: "text-rose-400",
};
function fmtTime(ts) {
    return new Date(ts).toISOString().slice(11, 23);
}
function EntryRow({ entry }) {
    const [open, setOpen] = useState(false);
    const statusEntry = entry.entries.find((e) => e.event === "request.end");
    const status = statusEntry?.status;
    const ms = statusEntry?.durationMs;
    const hasError = entry.entries.some((e) => e.level === "error");
    return (_jsxs("div", { className: "border-b border-slate-700 last:border-0", children: [_jsxs("button", { type: "button", onClick: () => setOpen((v) => !v), className: "flex w-full items-center gap-2 px-2 py-1 text-left hover:bg-slate-700/50", children: [_jsx("span", { className: "shrink-0 font-mono text-xs text-slate-400", children: fmtTime(entry.receivedAt) }), _jsx("span", { className: `shrink-0 text-xs font-semibold ${hasError ? "text-rose-400" : "text-slate-300"}`, children: entry.method }), _jsx("span", { className: "min-w-0 flex-1 truncate font-mono text-xs text-slate-200", children: entry.route }), status !== undefined && (_jsx("span", { className: `shrink-0 text-xs font-mono ${status >= 400 ? "text-rose-400" : "text-emerald-400"}`, children: status })), ms !== undefined && (_jsxs("span", { className: "shrink-0 text-xs text-slate-500", children: [ms, "ms"] })), _jsx("span", { className: "shrink-0 text-slate-500", children: open ? "▲" : "▼" })] }), open && (_jsx("ul", { className: "bg-slate-950/60 px-2 py-1 font-mono text-xs", children: entry.entries.map((e, i) => {
                    const { level, event, ts, ...rest } = e;
                    const ctx = Object.keys(rest).length > 0 ? rest : null;
                    return (_jsxs("li", { className: "flex gap-2 py-0.5", children: [_jsx("span", { className: "shrink-0 text-slate-600", children: fmtTime(ts) }), _jsx("span", { className: `shrink-0 uppercase ${LEVEL_STYLES[level] ?? "text-slate-300"}`, children: level.slice(0, 4) }), _jsx("span", { className: "text-slate-200", children: event }), ctx && (_jsx("span", { className: "text-slate-500 break-all", children: Object.entries(ctx)
                                    .map(([k, v]) => `${k}=${JSON.stringify(v)}`)
                                    .join(" ") }))] }, i));
                }) }))] }));
}
export function DebugPanel() {
    const { user } = useAuth();
    const { prefs } = usePreferences();
    const [, rerender] = useReducer((n) => n + 1, 0);
    const [minimized, setMinimized] = useState(false);
    const scrollRef = useRef(null);
    const debugDisabled = prefs.debugLogs === "disable";
    // Clear any in-memory entries the moment the user disables debug logging —
    // otherwise the panel re-mounts empty next time it renders but previously
    // captured logs would resurface if it ever re-enters the tree.
    useEffect(() => {
        if (debugDisabled)
            debugLogs.clear();
    }, [debugDisabled]);
    useEffect(() => {
        if (debugDisabled)
            return;
        return debugLogs.subscribe(() => {
            rerender();
            if (!minimized && scrollRef.current) {
                scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
            }
        });
    }, [minimized, debugDisabled]);
    // Only render for admin and super_admin, and only when the user hasn't
    // explicitly disabled debug logging in preferences.
    if (user?.role !== "super_admin" && user?.role !== "admin")
        return null;
    if (debugDisabled)
        return null;
    const all = debugLogs.getAll();
    if (all.length === 0 && minimized)
        return null;
    return (_jsxs("div", { className: "fixed bottom-0 right-0 z-50 flex w-full flex-col sm:w-[460px]", children: [_jsxs("div", { className: "flex items-center gap-2 bg-slate-800 px-3 py-1.5 text-xs text-slate-200", children: [_jsxs("span", { className: "mr-auto font-semibold tracking-wide", children: ["Debug Logs", all.length > 0 && ` (${all.length})`] }), _jsx("button", { type: "button", onClick: () => debugLogs.clear(), className: "rounded px-1.5 py-0.5 hover:bg-slate-700 text-slate-400 hover:text-slate-200", children: "Clear" }), _jsx("button", { type: "button", onClick: () => setMinimized((v) => !v), className: "rounded px-1.5 py-0.5 hover:bg-slate-700 text-slate-400 hover:text-slate-200", children: minimized ? "▲ Show" : "▼ Hide" })] }), !minimized && (_jsx("div", { ref: scrollRef, className: "max-h-72 overflow-y-auto bg-slate-900 text-slate-100", children: all.length === 0 ? (_jsx("p", { className: "px-3 py-4 text-center text-xs text-slate-500", children: "No requests yet. Make an API call to see logs." })) : (all.map((log, i) => _jsx(EntryRow, { entry: log }, `${log.requestId}-${i}`))) }))] }));
}
